import { Box, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ColletteButton, ColletteIconButton } from "@collette-travel/collette-common-controls";
import { useTranslations } from "next-intl";
import React from "react";

interface PrintModalProps {
  handleClose: () => void;
  handleOpen: () => void;
  tourName: string;
  dateRange: string;
  status: "building" | "ready" | "error";
}

const detailLabelSx = { color: "text.secondary", fontSize: "1.4rem", fontWeight: 500 };
const detailValueSx = { color: "text.primary", fontSize: "1.4rem", fontWeight: 500 };

const PrintModal = ({ handleClose, handleOpen, tourName, dateRange, status }: PrintModalProps) => {
  const t = useTranslations("PDP");
  let statusContent;

  if (status === "building") {
    statusContent = (
      <>
        <Box data-testid="print-modal-spinner">
          <CircularProgress size={32} />
        </Box>
        <Typography variant="h4" component="h2" data-testid="print-modal-title">
          {t("Building Your Itinerary")}
        </Typography>
        <Typography data-testid="print-modal-body" textAlign="center" color="text.secondary">
          {t("Building Your Itinerary Body")}
        </Typography>
      </>
    );
  } else if (status === "ready") {
    statusContent = (
      <Box data-testid="print-modal-ready" textAlign="center">
        <CheckCircleIcon
          data-testid="print-modal-success-icon"
          sx={{ fontSize: 32, color: "success.main" }}
        />
        <Typography variant="h4" component="h2" data-testid="print-modal-title">
          {t("Your itinerary is ready")}
        </Typography>
        <Typography data-testid="print-modal-body" textAlign="center" color="text.secondary">
          {t("Your Itinerary Ready Body")}
        </Typography>
      </Box>
    );
  } else {
    statusContent = (
      <Box data-testid="print-modal-error">
        <Typography variant="h4" component="h2" data-testid="print-modal-title">
          {t("We couldn't build your itinerary")}
        </Typography>
        <Typography data-testid="print-modal-body" textAlign="center" color="text.secondary">
          {t("Building Your Itinerary Error Body")}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack
      data-testid="print-modal-root"
      sx={{
        p: "3.2rem",
        gap: "2.4rem",
        alignItems: "center",
        width: "100%",
        position: "relative"
      }}
    >
      {statusContent}
      <Divider sx={{ width: "100%" }} />
      <Stack gap="0.8rem" width="100%">
        <Stack direction="row" justifyContent="space-between">
          <Typography data-testid="print-modal-tour-label" sx={detailLabelSx}>
            {t("Tour")}
          </Typography>
          <Typography data-testid="print-modal-tour-value" sx={detailValueSx}>
            {tourName}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Typography data-testid="print-modal-dates-label" sx={detailLabelSx}>
            {t("Dates")}
          </Typography>
          <Typography data-testid="print-modal-dates-value" sx={detailValueSx}>
            {dateRange}
          </Typography>
        </Stack>
      </Stack>
      {status === "building" && (
        <Typography data-testid="print-modal-footer" color="text.disabled" fontSize="1.4rem">
          {t("Building Your Itinerary Footer")}
        </Typography>
      )}
      {status === "ready" && (
        <ColletteButton
          testId="print-modal-open-button"
          label={t("View itinerary")}
          variant="contained"
          size="large"
          onClick={handleOpen}
        />
      )}
      <ColletteIconButton
        testId="print-modal-close-button"
        onClick={handleClose}
        sx={{ position: "absolute", top: "1.6rem", right: "1.6rem" }}
      >
        <Close />
      </ColletteIconButton>
    </Stack>
  );
};

export default PrintModal;
